import { Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import type { CurrentAccount } from '../../api/types/reports';
import { CurrentAccountAutocomplete } from '../CurrentAccountAutocomplete';
import { formatAccountBalance } from './billingInfo';

export interface BillingAccountValue {
  currentAccount: CurrentAccount | null;
  billingName: string;
  identityOrTaxNo: string;
}

export const emptyBillingAccountValue: BillingAccountValue = {
  currentAccount: null,
  billingName: '',
  identityOrTaxNo: '',
};

/**
 * "Cari Hesap / Fatura Bilgileri" kartı — uçuş akışındaki (`AirPassengerForm`) kutunun
 * paylaşılan hâli. Kurumsal cari hesap seçilirse `Billing/Submit`'e o hesabın vergi no'su
 * ile kurumsal fatura gider ve ödeme adımında "Cari ödeme" seçeneği açılır; seçilmezse
 * bireysel fatura için T.C. Kimlik / Vergi No girilir. Değerden `BillingInformation`
 * kurmak için `buildBillingInformation` (billingInfo.ts).
 */
export function BillingAccountCard({
  value,
  onChange,
}: {
  value: BillingAccountValue;
  onChange: (value: BillingAccountValue) => void;
}) {
  const { currentAccount, billingName, identityOrTaxNo } = value;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Cari Hesap / Fatura Bilgileri
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kurumsal cari hesap üzerinden ödeme yapılacaksa hesabı seçin (opsiyonel) — seçilirse bakiyesi
          otomatik gösterilir, fatura adı/vergi no arka planda hesaptan doldurulur ve ödeme adımında
          "Cari ödeme" seçeneği açılır. Cari hesap seçilmezse bireysel fatura için T.C. Kimlik / Vergi No
          girebilirsiniz.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CurrentAccountAutocomplete
              value={currentAccount}
              onChange={(account) =>
                onChange({
                  ...value,
                  currentAccount: account,
                  billingName: account ? account.title1 || account.title2 || '' : billingName,
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            {currentAccount ? (
              <TextField
                fullWidth
                size="small"
                label="Bakiye"
                value={formatAccountBalance(currentAccount.limit)}
                slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Fatura Adı"
                slotProps={{ inputLabel: { shrink: true } }}
                value={billingName}
                onChange={(e) => onChange({ ...value, billingName: e.target.value })}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="T.C. Kimlik / Vergi No"
              disabled={!!currentAccount}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={currentAccount ? "Cari hesabın vergi no'su kullanılıyor" : undefined}
              value={currentAccount ? '' : identityOrTaxNo}
              onChange={(e) => onChange({ ...value, identityOrTaxNo: e.target.value })}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
